import React, { useState, useEffect } from 'react';
import { Play, Clock, Search, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Videos = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/admin/videos/videos?published_only=true`);
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVideos = videos.filter(video =>
    video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    video.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const videoCategories = [...new Set(videos.map(v => v.category))];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              <span className="text-gradient">Video Resource Center</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Our video library features educational content focused on truck accident prevention, 
              safety best practices, and legal awareness.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Video Categories Info */}
      <section className="py-8 bg-gray-900 border-b border-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white mb-2">Videos Include:</h2>
              <div className="flex flex-wrap gap-2">
                {[
                  'Safety training clips',
                  'Accident analysis and lessons learned',
                  'Legal and compliance discussions',
                  'Industry expert insights'
                ].map((item, index) => (
                  <span key={index} className="text-sm text-gray-400 bg-gray-800 px-3 py-1 rounded-full">
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div className="mt-4 md:mt-0">
              <p className="text-sm text-orange-400 font-medium">
                New videos are added regularly
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="section-padding bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="mb-6">
            <p className="text-gray-400">
              Showing {filteredVideos.length} {filteredVideos.length === 1 ? 'video' : 'videos'}
            </p>
          </div>
          
          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVideos.map((video) => (
                <Card key={video.id} className="card-hover bg-gray-800 border-gray-700 overflow-hidden group">
                  <div className="relative aspect-video overflow-hidden video-overlay cursor-pointer">
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/60 transition-colors">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-orange-500 group-hover:bg-orange-600 transition-colors">
                        <Play className="h-8 w-8 text-white ml-1" fill="white" />
                      </div>
                    </div>
                    <div className="absolute top-3 right-3 bg-black/80 px-2 py-1 rounded text-xs text-white flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {video.duration}
                    </div>
                  </div>
                  <CardHeader>
                    <div className="badge badge-orange mb-2">{video.category}</div>
                    <CardTitle className="text-white text-xl group-hover:text-orange-400 transition-colors">
                      {video.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400">
                      {video.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No videos found matching your search.</p>
              <Button 
                variant="outline" 
                className="mt-4 border-orange-500 text-orange-400 hover:bg-orange-500/10"
                onClick={() => setSearchQuery('')}
              >
                Clear Search
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Want More Educational Content?
            </h2>
            <p className="text-gray-300 mb-6">
              Subscribe to our newsletter to be notified when new videos are published.
            </p>
            <Button size="lg" className="btn-primary text-white font-semibold px-8 h-12">
              Subscribe Now
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Videos;
